import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { SchoolSettingsResponse, SchoolSettingsUpdateRequest, EmailSettings, EmailSettingsResponse } from '../types';

/**
 * School Settings API Service
 */
export const settingsApi = {
    /**
     * Get current school settings via GET /schools/public/{slug}
     */
    getSettings: async (schoolSlug: string): Promise<SchoolSettingsResponse> => {
        try {
            const response = await apiClient.get<SchoolSettingsResponse>(API_ENDPOINTS.SCHOOLS.PUBLIC(schoolSlug));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch school settings'
            };
        }
    },

    /**
     * Update school settings via PATCH /upload/{slug}/settings/ (multipart/form-data)
     */
    updateSettings: async (schoolSlug: string, data: SchoolSettingsUpdateRequest): Promise<SchoolSettingsResponse> => {
        try {
            const formData = new FormData();

            if (data.name !== undefined) formData.append('name', data.name);
            if (data.primary_color !== undefined) formData.append('primary_color', data.primary_color);
            if (data.accent_color !== undefined) formData.append('accent_color', data.accent_color);
            if (data.tagline !== undefined) formData.append('tagline', data.tagline);
            if (data.support_email !== undefined) formData.append('support_email', data.support_email);
            if (data.support_phone !== undefined) formData.append('support_phone', data.support_phone);
            if (data.logo) formData.append('logo', data.logo);
            if (data.banner) formData.append('banner', data.banner);

            const config = { headers: { 'Content-Type': 'multipart/form-data' } };

            const response = await apiClient.patch<SchoolSettingsResponse>(
                API_ENDPOINTS.SCHOOL_SETTINGS.UPDATE(schoolSlug),
                formData,
                config
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update school settings'
            };
        }
    },

    /**
     * Get email SMTP settings via GET /:schoolSlug/email-config/
     */
    getEmailConfig: async (schoolSlug: string): Promise<EmailSettingsResponse> => {
        try {
            const response = await apiClient.get<EmailSettingsResponse>(API_ENDPOINTS.SCHOOL_SETTINGS.EMAIL_CONFIG(schoolSlug));
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to fetch email settings'
            };
        }
    },

    /**
     * Update/Configure email SMTP settings via POST /:schoolSlug/email-config/
     */
    updateEmailConfig: async (schoolSlug: string, data: EmailSettings): Promise<EmailSettingsResponse> => {
        try {
            const response = await apiClient.post<EmailSettingsResponse>(
                API_ENDPOINTS.SCHOOL_SETTINGS.EMAIL_CONFIG(schoolSlug),
                data
            );
            return response.data;
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.error || error.response?.data?.message || 'Failed to update email settings'
            };
        }
    }
};
